// Dashboard.jsx - Updated Operator Modal Section
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';
import OrderDetailsModal from '../../components/order-details-modal';
import RetailerProfileModal from '../../components/retailer-profile-modal';
import { FaArrowUp, FaArrowDown, FaSearch, FaTimes, FaChevronDown } from "react-icons/fa";

// All categories list
const ALL_CATEGORIES = [
    'Broadband Postpaid',
    'Cable TV',
    'Clubs and Associations',
    'Credit Card',
    'DTH',
    'Education Fees',
    'Electricity',
    'Fastag',
    'Gas',
    'Health Insurance',
    'Hospital',
    'Hospital and Pathology',
    'Housing Society',
    'Insurance',
    'Landline Postpaid',
    'Life Insurance',
    'Loan Repayment',
    'LPG Gas',
    'Mobile Postpaid',
    'Municipal Services',
    'Municipal Taxes',
    'Postpaid',
    'Prepaid',
    'Recurring Deposit',
    'Redeem Code',
    'Subscription',
    'Water',
    'Donation',
    'Prepaid Meter'
];

function TransactionBillPayment() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const [providers, setProviders] = useState([]);
    const [operators, setOperators] = useState([]);
    const [filteredOperators, setFilteredOperators] = useState([]);
    const [operatorSearch, setOperatorSearch] = useState('');
    const [selectedOperatorName, setSelectedOperatorName] = useState('');
    
    // Operator Modal states
    const [isOperatorModalOpen, setIsOperatorModalOpen] = useState(false);
    const [operatorModalSearch, setOperatorModalSearch] = useState('');
    const [operatorModalList, setOperatorModalList] = useState([]);
    const [operatorModalLoading, setOperatorModalLoading] = useState(false);

    // Modal states
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [retailerModalOpen, setRetailerModalOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchBy: 'mobileNumber',
        searchValue: '',
        dateRange: {
            start: new Date().toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0]
        },
        amountMin: '',
        amountMax: '',
        status: '',
        operator: '',
        provider: ''
    });

    // Status options with IDs
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: 'Success', name: 'Success' },
        { id: 'Failed', name: 'Failed' },
        { id: 'Pending', name: 'Pending' },
        { id: 'Initiated', name: 'Initiated' }
    ];

    // Search options
    const searchOptions = [
        { value: 'mobileNumber', label: 'Mobile Number' },
        { value: 'retailerNumber', label: 'Retailer Number' },
        { value: 'transactionId', label: 'Transaction ID' },
        { value: 'orderId', label: 'Order ID' },
        { value: 'clientOrderId', label: 'Client Order ID' }
    ];

    // Fetch Providers
    useEffect(() => {
        const fetchProviders = async () => {
            try {
                const result = await apiCall(
                    "provider",
                    {
                        category: "1"
                    },
                    true
                );

                if (result.status === true) {
                    const providerList = [
                        { id: '', name: 'All Providers' },
                        ...result.provider.map(provider => ({
                            id: provider.id,
                            name: provider.name
                        }))
                    ];
                    setProviders(providerList);
                } else {
                    toast.error(result.message);
                }
            } catch (error) {
                toast.error("Network error. Please try again.");
            }
        };
        fetchProviders();
    }, []);

    // Fetch Operators with typing search
    const fetchOperators = async (typing = '') => {
        try {
            const result = await apiCall(
                "operators",
                {
                    typing: typing,
                    category: ALL_CATEGORIES,
                    sub_category: 'BBPS'
                },
                true
            );

            if (result.status === true) {
                const operatorList = [
                    { id: '', name: 'All Operators' },
                    ...result.operators.map(operator => ({
                        id: operator.operator_id,
                        name: operator.biller_name,
                        logo: operator.logo
                    }))
                ];
                setOperators(operatorList);
                setFilteredOperators(operatorList);
                return operatorList;
            } else {
                const defaultList = [{ id: '', name: 'All Operators', logo: '' }];
                setOperators(defaultList);
                setFilteredOperators(defaultList);
                return defaultList;
            }
        } catch (error) {
            const defaultList = [{ id: '', name: 'All Operators', logo: '' }];
            setOperators(defaultList);
            setFilteredOperators(defaultList);
            return defaultList;
        }
    };

    // Initial operators fetch
    useEffect(() => {
        fetchOperators('');
    }, []);

    // Open operator modal
    const openOperatorModal = async () => {
        setIsOperatorModalOpen(true);
        setOperatorModalSearch('');
        setOperatorModalLoading(true);
        
        // Fetch all operators when modal opens
        const result = await fetchOperators('');
        setOperatorModalList(result || []);
        setOperatorModalLoading(false);
    };

    // Close operator modal
    const closeOperatorModal = () => {
        setIsOperatorModalOpen(false);
        setOperatorModalSearch('');
    };

    // Handle operator search in modal
    const handleOperatorModalSearch = async (value) => {
        setOperatorModalSearch(value);
        setOperatorModalLoading(true);
        
        const result = await fetchOperators(value);
        setOperatorModalList(result || []);
        setOperatorModalLoading(false);
    };

    // Select operator from modal
    const selectOperatorFromModal = (operator) => {
        handleFilterChange('operator', operator.id);
        setSelectedOperatorName(operator.name);
        setOperatorSearch(operator.name);
        closeOperatorModal();
    };

    // Clear operator selection
    const clearOperator = () => {
        handleFilterChange('operator', '');
        setSelectedOperatorName('');
        setOperatorSearch('');
        setFilteredOperators(operators);
    };

    // Fetch Transactions with pagination
    const fetchTransactions = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                startDate: filters.dateRange.start,
                endDate: filters.dateRange.end,
                status: filters.status || '',
                operator: filters.operator || '',
                provider: filters.provider || '',
                searchBy: filters.searchBy,
                searchValue: filters.searchValue,
                amountMin: filters.amountMin || '',
                amountMax: filters.amountMax || '',
                page: page,
                limit: pagination.perPage,
                order_type: '3'
            };

            const result = await apiCall(
                "transactions",
                params,
                true
            );

            if (result.status === true) {
                setTransactions(result.transactions || []);
                setFilteredTransactions(result.transactions || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch transactions");
                setTransactions([]);
                setFilteredTransactions([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setTransactions([]);
            setFilteredTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Status Recheck with SweetAlert2
    const handleStatusRecheck = (transaction) => {
        Swal.fire({
            title: 'Recheck Status?',
            text: `Do you want to recheck the status of order ${transaction.orderId}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Recheck!',
            cancelButtonText: 'Cancel',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);

                    const response = await apiCall(
                        "order-recheck-status",
                        {
                            order_id: transaction.orderId,
                            table: 'order'
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success(`Status rechecked successfully! New status: ${response.status_name || 'Updated'}`);
                        fetchTransactions(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to recheck status");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Handle Order ID Click - Open Order Details Modal
    const handleOrderIdClick = (orderId, userId) => {
        setSelectedOrderId(orderId);
        setSelectedUserId(userId);
        setOrderModalOpen(true);
    };

    // Handle Retailer Click - Open Retailer Profile Modal
    const handleRetailerClick = (userId) => {
        setSelectedUserId(userId);
        setRetailerModalOpen(true);
    };

    // Initial fetch
    useEffect(() => {
        fetchTransactions(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchTransactions(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchBy: 'mobileNumber',
            searchValue: '',
            dateRange: {
                start: new Date().toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0]
            },
            amountMin: '',
            amountMax: '',
            status: '',
            operator: '',
            provider: ''
        });
        setSelectedOperatorName('');
        setOperatorSearch('');
        fetchTransactions(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchTransactions(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        if (key === 'dateStart') {
            setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, start: value } }));
        } else if (key === 'dateEnd') {
            setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, end: value } }));
        } else {
            setFilters(prev => ({ ...prev, [key]: value }));
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Success': 'bg-green-100 text-green-800',
            'Failed': 'bg-red-100 text-red-800',
            'Pending': 'bg-yellow-100 text-yellow-800',
            'Initiated': 'bg-blue-100 text-blue-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Get operator name by ID
    const getOperatorName = (operatorId) => {
        const operator = operators.find(op => op.id === operatorId);
        return operator ? operator.name : operatorId;
    };

    // Get provider name by ID
    const getProviderName = (providerId) => {
        const provider = providers.find(prov => prov.id === providerId);
        return provider ? provider.name : providerId;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Transaction History</h1>
                        <p className="text-gray-600 text-sm">View and manage all bill payment transactions</p>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                {/* Column 1: Search By + Search Value */}
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Search By
                                        </label>
                                        <select
                                            value={filters.searchBy}
                                            onChange={(e) => handleFilterChange('searchBy', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            {searchOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Search Value
                                        </label>
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder={`Enter ${searchOptions.find(opt => opt.value === filters.searchBy)?.label || 'search'}...`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Column 2: Date Range + Amount Range */}
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date Range
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="date"
                                                value={filters.dateRange.start}
                                                onChange={(e) => handleFilterChange('dateStart', e.target.value)}
                                                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                            <input
                                                type="date"
                                                value={filters.dateRange.end}
                                                onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
                                                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount Range (₹)
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="number"
                                                min="10"
                                                max="10000"
                                                value={filters.amountMin}
                                                onChange={(e) => handleFilterChange('amountMin', e.target.value)}
                                                placeholder="Min"
                                                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                            <input
                                                type="number"
                                                min="10"
                                                max="10000"
                                                value={filters.amountMax}
                                                onChange={(e) => handleFilterChange('amountMax', e.target.value)}
                                                placeholder="Max"
                                                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Column 3: Status + Operator (Modal based) */}
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            value={filters.status}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            {statusOptions.map(status => (
                                                <option key={status.id} value={status.id}>
                                                    {status.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Operator
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={selectedOperatorName}
                                                readOnly
                                                placeholder="Click to search operator..."
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer text-sm"
                                                onClick={openOperatorModal}
                                            />
                                            {filters.operator && (
                                                <button
                                                    onClick={clearOperator}
                                                    className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                                    title="Clear operator"
                                                >
                                                    <FaTimes />
                                                </button>
                                            )}
                                            <button
                                                onClick={openOperatorModal}
                                                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                <FaSearch />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Column 4: Provider + Actions */}
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Provider
                                        </label>
                                        <select
                                            value={filters.provider}
                                            onChange={(e) => handleFilterChange('provider', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            {providers.map(provider => (
                                                <option key={provider.id} value={provider.id}>
                                                    {provider.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            onClick={applyFilters}
                                            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                        >
                                            Apply
                                        </button>
                                        <button
                                            onClick={resetFilters}
                                            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredTransactions.length}</span> transactions
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Transaction Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Order ID
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date & Time
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Mobile
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Retailer
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Revenue
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Operator
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Provider
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTransactions.length > 0 ? (
                                        filteredTransactions.map((transaction) => (
                                            <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td
                                                    className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600 cursor-pointer hover:underline hover:text-blue-800"
                                                    onClick={() => handleOrderIdClick(transaction.orderId, transaction.userId)}
                                                >
                                                    {transaction.orderId}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {transaction.orderDate || formatDate(transaction.orderDate)}
                                                    <br />
                                                    <span className="text-xs text-gray-400">
                                                        {transaction.orderTime || ''}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {transaction.cn}
                                                </td>
                                                <td
                                                    className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 cursor-pointer hover:text-blue-600"
                                                    onClick={() => handleRetailerClick(transaction.userId)}
                                                >
                                                    <div className="flex flex-col">
                                                        <span className="hover:underline">{transaction.retailerName}</span>
                                                        <span className="text-xs text-gray-400">{transaction.retailerMobile}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                    ₹{parseFloat(transaction.amount).toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold">
                                                    {parseFloat(transaction.adminProfit) > 0 ? (
                                                        <span className="flex items-center text-green-600">
                                                            <FaArrowUp className="mr-1" />
                                                            ₹{parseFloat(transaction.adminProfit).toFixed(2)}
                                                        </span>
                                                    ) : parseFloat(transaction.adminProfit) < 0 ? (
                                                        <span className="flex items-center text-red-600">
                                                            <FaArrowDown className="mr-1" />
                                                            ₹{Math.abs(parseFloat(transaction.adminProfit)).toFixed(2)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-900">
                                                            ₹0.00
                                                        </span>
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 whitespace-nowrap cursor-pointer"
                                                    onClick={() => handleStatusRecheck(transaction)}
                                                >
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.statusId)} hover:opacity-80 transition-opacity`}>
                                                        {transaction.statusId}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        {transaction.operatorLogo && (
                                                            <img
                                                                src={transaction.operatorLogo}
                                                                alt={transaction.operatorName || getOperatorName(transaction.operatorId)}
                                                                className="w-6 h-6 rounded-full object-contain"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        )}
                                                        <span>{transaction.operatorName || getOperatorName(transaction.operatorId)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {transaction.providerName || getProviderName(transaction.providerId)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No transactions found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                {/* Page numbers */}
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Operator Search Modal - Fixed Height */}
            {isOperatorModalOpen && (
                <div className="fixed h-300 inset-0 z-50 overflow-y-auto">
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                        onClick={closeOperatorModal}
                    ></div>

                    {/* Modal */}
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden">
                            {/* Header - Fixed */}
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-gray-900">Search Operator</h3>
                                <button
                                    onClick={closeOperatorModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Search Input - Fixed */}
                            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={operatorModalSearch}
                                        onChange={(e) => handleOperatorModalSearch(e.target.value)}
                                        placeholder="Type to search operators..."
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {/* Results - Scrollable */}
                            <div className="flex-1 overflow-y-auto p-4 min-h-[200px] max-h-[400px]">
                                {operatorModalLoading ? (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                                        <p className="mt-4 text-gray-600 text-sm">Loading operators...</p>
                                    </div>
                                ) : operatorModalList.length > 0 ? (
                                    <div className="space-y-2">
                                        {operatorModalList.map((operator) => (
                                            <div
                                                key={operator.id}
                                                onClick={() => selectOperatorFromModal(operator)}
                                                className={`px-4 py-3 rounded-lg cursor-pointer flex items-center gap-3 transition-colors ${
                                                    filters.operator === operator.id 
                                                        ? 'bg-blue-50 border-2 border-blue-500' 
                                                        : 'hover:bg-gray-50 border-2 border-transparent'
                                                }`}
                                            >
                                                {operator.logo && (
                                                    <img
                                                        src={operator.logo}
                                                        alt={operator.name}
                                                        className="w-8 h-8 rounded-full object-contain flex-shrink-0"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{operator.name}</p>
                                                </div>
                                                {filters.operator === operator.id && (
                                                    <span className="text-blue-600 flex-shrink-0">
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <p className="text-gray-500">No operators found</p>
                                        <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer - Fixed */}
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center">
                                <span className="text-xs text-gray-500">
                                    {operatorModalList.length > 0 && `${operatorModalList.length} operators found`}
                                </span>
                                <button
                                    onClick={closeOperatorModal}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            <OrderDetailsModal
                isOpen={orderModalOpen}
                onClose={() => setOrderModalOpen(false)}
                orderId={selectedOrderId}
            />

            <RetailerProfileModal
                isOpen={retailerModalOpen}
                onClose={() => setRetailerModalOpen(false)}
                userId={selectedUserId}
            />

            <Toaster position="top-right" />
        </div>
    );
}

export default TransactionBillPayment;